/**
 * Coolman Workforce — shared API helpers, formatters, and constants
 * used across the workforce tabs. Kept at the top of the module tree so
 * page + component code stay focused on presentation.
 */
import { api } from "@/lib/api";

export const WORKFORCE_TABS = [
  { key: "dashboard",   label: "Dashboard" },
  { key: "calendar",    label: "Calendar" },
  { key: "events",      label: "Events" },
  { key: "employees",   label: "Employees" },
  { key: "coverage",    label: "Coverage" },
  { key: "work_groups", label: "Work Groups" },
  { key: "holidays",    label: "Holidays" },
  { key: "settings",    label: "Settings" },
];

export const STATUS_TONE = {
  Approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  Pending:  "bg-amber-50 text-amber-800 border-amber-200",
  Declined: "bg-rose-50 text-rose-800 border-rose-200",
};

// -------- API helpers --------
export const workforceApi = {
  metadata:      () => api.get("/workforce/metadata"),
  employees:     () => api.get("/workforce/employees"),
  createEmp:     (body) => api.post("/workforce/employees", body),
  updateEmp:     (id, body) => api.put(`/workforce/employees/${id}`, body),
  deleteEmp:     (id) => api.delete(`/workforce/employees/${id}`),
  syncUsers:     () => api.post("/workforce/employees/sync-from-users"),
  eventTypes:    () => api.get("/workforce/event-types"),
  saveEventType: (code, body) => api.put(`/workforce/event-types/${code}`, body),
  events:        (params) => api.get("/workforce/events", { params }),
  createEvent:   (body) => api.post("/workforce/events", body),
  updateEvent:   (id, body) => api.put(`/workforce/events/${id}`, body),
  deleteEvent:   (id) => api.delete(`/workforce/events/${id}`),
  holidays:      () => api.get("/workforce/holidays"),
  saveHoliday:   (body) => api.post("/workforce/holidays", body),
  updateHoliday: (id, body) => api.put(`/workforce/holidays/${id}`, body),
  patchHoliday:  (id, body) => api.patch(`/workforce/holidays/${id}`, body),
  deleteHoliday: (id) => api.delete(`/workforce/holidays/${id}`),
  refreshHolidays: (year, providerKey) => api.post("/workforce/holidays/refresh", { year, provider_key: providerKey || null }),
  holidayProviders: () => api.get("/workforce/holidays/providers"),
  holidayOverview: () => api.get("/workforce/holidays/overview"),
  holidayRefreshes: () => api.get("/workforce/holidays/refreshes"),
  calendar: (params) => api.get("/workforce/calendar", { params }),
  balances:      () => api.get("/workforce/balances"),
  dashboard:     () => api.get("/workforce/dashboard"),
  operationsDashboard: () => api.get("/workforce/dashboard/operations"),
  // Work Groups (WF-102)
  workGroups:            (includeArchived) => api.get("/workforce/work-groups", { params: { include_archived: !!includeArchived } }),
  workGroup:             (code) => api.get(`/workforce/work-groups/${code}`),
  createWorkGroup:       (body) => api.post("/workforce/work-groups", body),
  updateWorkGroup:       (code, body) => api.put(`/workforce/work-groups/${code}`, body),
  archiveWorkGroup:      (code) => api.post(`/workforce/work-groups/${code}/archive`),
  unarchiveWorkGroup:    (code) => api.post(`/workforce/work-groups/${code}/unarchive`),
  workGroupMigrationReport: () => api.get("/workforce/work-groups/migration-report"),
  // Employee Profile (WF-101)
  employeeProfile:       (id) => api.get(`/workforce/employees/${id}/profile`),
  // Operational Coverage (WF-107 · Wave 3)
  coverageRoster:            () => api.get("/workforce/coverage/roster"),
  updateCoverageRoster:      (body) => api.put("/workforce/coverage/roster", body),
  coverageRegenerate:        () => api.post("/workforce/coverage/rotations/generate"),
  coverageRotations:         (params) => api.get("/workforce/coverage/rotations", { params }),
  coverageCurrent:           () => api.get("/workforce/coverage/current"),
  coverageUpcoming:          (limit) => api.get("/workforce/coverage/upcoming", { params: { limit } }),
  coverageReplace:           (body) => api.post("/workforce/coverage/exceptions/replace", body),
  coverageSwap:              (body) => api.post("/workforce/coverage/exceptions/swap", body),
  coverageUndoException:     (rotationId) => api.delete(`/workforce/coverage/exceptions/${rotationId}`),
  coverageExceptionHistory:  (limit) => api.get("/workforce/coverage/exceptions", { params: { limit } }),
  // Per-widget dashboard endpoints (spec 13 § 4). Each is independently
  // callable so widgets refresh in isolation without blocking each other.
  dashboardAwayToday:          () => api.get("/workforce/dashboard/away-today"),
  dashboardUpcomingTimeOff:    () => api.get("/workforce/dashboard/upcoming-time-off"),
  dashboardPendingRequests:    () => api.get("/workforce/dashboard/pending-requests"),
  dashboardUpcomingBirthdays:  () => api.get("/workforce/dashboard/upcoming-birthdays"),
  dashboardCompanyHolidays:    () => api.get("/workforce/dashboard/company-holidays"),
  dashboardOnCall:             () => api.get("/workforce/dashboard/on-call"),
  dashboardRecentActivity:     () => api.get("/workforce/dashboard/recent-activity"),
  dashboardEmployeeBalances:   () => api.get("/workforce/dashboard/employee-balances"),
  uploadAttach:  (eventId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post(`/workforce/events/${eventId}/attachments`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  removeAttach:  (eventId, attachId) =>
    api.delete(`/workforce/events/${eventId}/attachments/${attachId}`),
};

// -------- Formatters --------
export function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "2-digit" });
}

export function formatDateRange(start, end, startHalf, endHalf) {
  if (start === end) {
    const bit = startHalf || endHalf ? " (½ day)" : "";
    return `${formatDate(start)}${bit}`;
  }
  const openBit = startHalf ? " (½ day)" : "";
  const closeBit = endHalf ? " (½ day)" : "";
  return `${formatDate(start)}${openBit} → ${formatDate(end)}${closeBit}`;
}

export function formatDays(n) {
  if (n === null || n === undefined) return "—";
  return Number(n).toFixed(n % 1 === 0 ? 0 : 1);
}

const MONTH_ABBREV = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Format a MM-DD birthday for display as "MMM DD" (e.g. "06-11" → "Jun 11").
 * Per WORKFORCE_REQUIREMENTS.md § 4.5, only month + day are stored/displayed —
 * birth year is intentionally excluded.
 */
export function formatBirthday(mmdd) {
  if (!mmdd || typeof mmdd !== "string") return "—";
  const m = mmdd.match(/^(\d{2})-(\d{2})$/);
  if (!m) return mmdd;
  const monthIdx = parseInt(m[1], 10) - 1;
  const day = m[2];
  if (monthIdx < 0 || monthIdx > 11) return mmdd;
  return `${MONTH_ABBREV[monthIdx]} ${day}`;
}
